import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import Topbar from '@/layout/Topbar';
import { useShell } from '@/layout/AppShell';
import { useCan } from '@/auth/AuthContext';
import { useUrlState } from '@/lib/useUrlState';
import { ApiError, api } from '@/lib/api';
import RoomEditModal from './RoomEditModal';

const URL_SCHEMA = { archive: '0', edit: '' };

export default function RoomsPage() {
  const { tweaks, updateTweak } = useShell();
  const can = useCan();
  const canWrite  = can('rooms', 'write');
  const canDelete = can('rooms', 'delete');

  const [params, setParams] = useUrlState(URL_SCHEMA);
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);

  const reload = () => {
    setLoading(true); setError(null);
    const qs = params.archive === '1' ? '?include_archive=1' : '';
    api.get('/api/rooms' + qs)
      .then((res) => setRows(res.rows || []))
      .catch((e) => setError(e instanceof ApiError ? e.message : 'Не удалось загрузить'))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [params.archive]);

  const onDelete = async (r) => {
    if (!canDelete) return;
    if (!confirm(`Удалить кабинет «${r.name}»?\nЕсли он используется в расписании — лучше архивировать.`)) return;
    try {
      await api.delete('/api/rooms/' + r.id);
      setRows((prev) => prev.filter((x) => x.id !== r.id));
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Не удалось удалить');
    }
  };

  const editing = (() => {
    if (!params.edit) return null;
    if (params.edit === 'new') return 'new';
    return rows.find((r) => r.id === parseInt(params.edit, 10)) || null;
  })();

  return (
    <>
      <Topbar
        breadcrumb={['Обучение', 'Кабинеты']}
        density={tweaks.density}
        onDensityChange={(d) => updateTweak('density', d)}
      />
      <div className="ref-page">
        <div className="ref-toolbar">
          <span className="ref-toolbar__title">
            Кабинеты {!loading && <span className="muted">· {rows.length}</span>}
          </span>
          <select
            value={params.archive || '0'}
            onChange={(e) => setParams({ archive: e.target.value })}
            style={{
              marginLeft: 14, height: 30, padding: '0 8px',
              border: '1px solid var(--border-strong)', borderRadius: 6,
              fontSize: 13, background: 'var(--panel)', color: 'var(--fg)',
            }}
          >
            <option value="0">Активные</option>
            <option value="1">Все, включая архив</option>
          </select>
          <div className="ref-toolbar__spacer" />
          <button
            className="btn btn--primary btn--sm"
            onClick={() => setParams({ edit: 'new' })}
            disabled={!canWrite}
            title={canWrite ? undefined : 'Нет прав на запись'}
          >
            <Icon name="plus" size={13} /> Добавить кабинет
          </button>
        </div>

        <div className="ref-content">
          {error && <div style={{ color: 'var(--red-fg)', padding: 16 }}>Ошибка: {error}</div>}
          {loading ? (
            <div className="ref-empty">Загрузка…</div>
          ) : rows.length === 0 ? (
            <div className="ref-empty">Кабинетов нет. Добавьте первый.</div>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Кабинет</th>
                    <th>Описание</th>
                    <th style={{ width: 100 }}>Статус</th>
                    {(canWrite || canDelete) && <th style={{ width: 80 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id}
                        onClick={() => canWrite && setParams({ edit: String(r.id) })}
                        style={{ cursor: canWrite ? 'pointer' : 'default', opacity: r.archive ? 0.6 : 1 }}>
                      <td><span style={{ fontWeight: 500 }}>{r.name}</span></td>
                      <td className="muted">{r.description || <span style={{ color: 'var(--fg-faint)' }}>—</span>}</td>
                      <td>
                        {r.archive
                          ? <span className="sb sb--archived">Архив</span>
                          : <span className="sb sb--active">Активен</span>}
                      </td>
                      {(canWrite || canDelete) && (
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="ref-row-actions">
                            {canWrite && (
                              <button onClick={() => setParams({ edit: String(r.id) })} title="Редактировать">
                                <Icon name="edit" size={14} />
                              </button>
                            )}
                            {canDelete && (
                              <button className="is-danger" onClick={() => onDelete(r)} title="Удалить">
                                <Icon name="x" size={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <RoomEditModal
          mode={editing === 'new' ? 'create' : 'edit'}
          room={editing === 'new' ? null : editing}
          onClose={() => setParams({ edit: '' })}
          onSaved={(r) => {
            setRows((prev) => {
              const i = prev.findIndex((x) => x.id === r.id);
              if (i >= 0) { const n = prev.slice(); n[i] = r; return n; }
              return [...prev, r];
            });
          }}
        />
      )}
    </>
  );
}
